'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useMemo } from 'react'

import { IS_BRAND_PULSZ } from 'config/brands'
import PWAProvider from 'containers/PWAProvider'
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect'
import Slots from 'pageContainers/AllGames/Games'
import BuilderTextContent from 'src/components/BuilderTextContent'
import Loader from 'src/components/Loader'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { setTabsConfig } from 'src/store/modules/user/actions'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { saveGameProducts } from 'store/modules/games/actions'
import { filterBingoGamesByMode } from 'utils/filterBingoGamesByMode'

import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'

export type Props = {
  games: GameProduct[]
  title?: string
  content?: any
  categories?: string[]
  shouldBeRedirected?: boolean
  layout?: string
}

export default function GamesCategoryPageClient({
  games,
  content,
  categories,
  shouldBeRedirected,
  title,
  layout,
}: Props) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const activeCurrency = useAppSelector(getActiveCurrencyIdSelector)
  const filteredGames = useMemo(
    () => (IS_BRAND_PULSZ ? games : filterBingoGamesByMode(games, activeCurrency, { excludeBingo: true })),
    [activeCurrency, games],
  )

  useIsomorphicLayoutEffect(() => {
    if (!categories) {
      return
    }

    dispatch(setTabsConfig({ tabsConfig: { categories, shouldBeRedirected } }))
  }, [categories, dispatch, shouldBeRedirected])

  useEffect(() => {
    if (filteredGames.length === 0 || shouldBeRedirected) {
      router.push('/slots')

      return
    }

    dispatch(saveGameProducts({ games: filteredGames }))
  }, [dispatch, filteredGames, router, shouldBeRedirected])

  if (shouldBeRedirected) {
    return <Loader fullscreen />
  }

  return (
    <>
      <Slots games={filteredGames} categories={categories} title={title} layout={layout} />
      {content && <BuilderTextContent content={content} model={process.env.CATEGORIES_DESCRIPTIONS} />}
      <PWAProvider />
    </>
  )
}
